// [목적] 폴리오 피드 무한스크롤 훅 — ExplorePage/HomePage/MyFoliosPage 에서 listFolios cursor 페이지네이션
// [이유] FEED-04/05 — ExplorePage sort 토글 + HomePage 추천 피드를 단일 훅으로 처리.
//         sort 파라미터 변경 시 쿼리 키가 달라져 캐시가 자동 분리됨.
//         fe-be-gap-tier1 T6(ADR-TR-8): BE folios.repository 가 sort 파라미터를 지원하므로
//         클라이언트 정렬 폴백(sortFolios/beSort/_clientSort) 을 제거하고 sort 를 BE 로 직전달.
//         listFoliosQuerySchema.sort 가 'latest'|'likes'|'forks' 를 허용하므로 타입 캐스트 불필요.
//         queryKeys.folioList 가 params 통째 키로 동작 — sort 별 캐시 자동 분리.
//         M7 P1(발견 화면 서버 검색·필터): tags(복수)·minDurationDays·maxDurationDays·q 옵션 추가 — ExplorePage
//         필터 모달(기간·테마) + 검색어를 BE listFoliosQuerySchema 로 직전달(클라이언트 필터 제거).
//         M7 P1(내 폴리오 서버 필터): visibility 옵션 추가 — MyFoliosPage all/public/private 탭이
//         FoliosService.list author==='me' 분기(BE 이미 지원, M6 P2)로 서버 필터링.
// [사이드 이팩트] sort/tags/minDurationDays/maxDurationDays/q/visibility 변경 시 queryKey 변경 → 새 캐시 엔트리 생성(이전 캐시 유지).
//                 'trending' sort 는 Explore select 3종에 없으므로 타입 보존만.
// [수정 시 고려사항] BE FoliosRepository.list sort 분기(latest/likes/forks)·키셋 커서 일반화 전제.
//                   author 파라미터는 MyFoliosPage 에서 본인 handle 전달용.
//                   sort='trending' 은 Explore select 에 없음 — 내부 폴백 없이 BE 전달됨(unknown 처리).
//                   tags 는 BE tagsQueryPreprocess 가 배열/콤마문자열 둘 다 허용 — buildQuery 의
//                   String(array) 직렬화(콤마 join)가 그대로 호환됨(folioClient.ts buildQuery 수정 불필요).

import { useInfiniteQuery } from '@tanstack/react-query';
import { listFolios } from '../api/folioClient.js';
import { queryKeys } from '../api/queryKeys.js';
import type { FolioList, ListFoliosQuery } from '@gangwoncanvas/types';

// ─── 타입 ──────────────────────────────────────────────────────────────────

/**
 * FolioFeedSort — 피드 정렬 옵션
 * - 'latest'   : 최신순 (BE created_at DESC, 기본값)
 * - 'likes'    : 좋아요순 (BE like_count DESC — fe-be-gap-tier1 T6, ADR-TR-8)
 * - 'forks'    : Fork순  (BE fork_count DESC — fe-be-gap-tier1 T6, ADR-TR-8)
 * - 'trending' : (레거시 타입 보존 — Explore select 미노출, BE 처리는 latest 동일)
 *
 * [변경] T6: BE sort 직결 — 클라이언트 정렬 폴백(sortFolios/beSort/_clientSort) 제거.
 *        listFoliosQuerySchema.sort = z.enum(['latest','likes','forks']) 으로 확정.
 *        queryKeys.folioList(params) 가 sort 포함 params 로 캐시 분리.
 */
export type FolioFeedSort = 'latest' | 'trending' | 'likes' | 'forks';

export interface UseFolioFeedOptions {
  sort?: FolioFeedSort;
  author?: string;
  tag?: string;
  /** M7 P1: 복수 태그 필터(vibe 배열과 overlap ANY 매칭) — ExplorePage 필터 모달 테마 멀티셀렉트 */
  tags?: string[];
  /** M7 P1: 여행 기간 하한(일) — duration_days >= minDurationDays */
  minDurationDays?: number;
  /** M7 P1: 여행 기간 상한(일) — duration_days <= maxDurationDays */
  maxDurationDays?: number;
  /** M7 P1: 검색어(title/subtitle/vibe ILIKE) — ExplorePage 검색창 서버 조회 */
  q?: string;
  /** M7 P1: 공개 범위 필터 — MyFoliosPage all/public/private 탭(author='me' 전용, BE M6 P2 지원) */
  visibility?: 'public' | 'link' | 'private';
  limit?: number;
  enabled?: boolean;
}

// ─── useFolioFeed ─────────────────────────────────────────────────────────────

/**
 * [목적] GET /folios cursor 무한스크롤 훅 — sort/author/tag 파라미터 지원, BE sort 직결
 * [이유] FEED-04 ExplorePage sort 토글 + FEED-05 HomePage 추천 피드 + FEED-06 MyFoliosPage 공용.
 *        fe-be-gap-tier1 T6(ADR-TR-8): BE listFoliosQuerySchema.sort 추가로 sort 를 BE 로 직접 전달.
 *        클라이언트 정렬 폴백(sortFolios/beSort/_clientSort) 제거 — 무한스크롤 페이지 경계 정렬 정합.
 * [사이드 이팩트] sort/author/tag 변경 시 queryKey 변경 → 새 캐시 엔트리 생성 (이전 캐시 유지).
 *                'likes'/'forks' 정렬은 BE ORDER BY + 키셋 커서(ADR-TR-3)로 페이지 경계 중복/누락 없음.
 * [수정 시 고려사항] limit 기본값 20. enabled=false 로 지연 로딩 가능.
 *                   'trending' 은 FolioFeedSort 에만 존재하고 ListFoliosQuery.sort enum 에 없음 —
 *                   buildQuery 가 문자열로 직렬화하므로 BE 에서 unknown sort 는 latest 로 폴백 처리됨.
 */
export function useFolioFeed(options: UseFolioFeedOptions = {}): {
  pages: FolioList[];
  allFolios: FolioList['items'];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const {
    sort = 'latest',
    author,
    tag,
    tags,
    minDurationDays,
    maxDurationDays,
    q,
    visibility,
    limit = 20,
    enabled = true,
  } = options;

  // [목적] BE 직전달 sort 파라미터 구성 — 클라이언트 정렬 폴백 제거(T6 ADR-TR-8)
  // [이유] listFoliosQuerySchema.sort = z.enum(['latest','likes','forks']) 으로 BE 가 직접 정렬 처리.
  //         ListFoliosQuery 타입이 sort?: 'latest'|'likes'|'forks' 이므로 'trending' 은 명시적 제외.
  // [사이드 이팩트] sort='trending' 은 buildQuery 가 'trending' 을 직렬화하고 BE 는 latest 로 폴백.
  // [수정 시 고려사항] sort 가 'trending' 일 경우 명시적으로 'latest' 로 매핑해도 됨 — 현재는 BE 폴백에 위임.
  const beSort: ListFoliosQuery['sort'] | undefined =
    sort === 'likes' || sort === 'forks' || sort === 'latest' ? sort : undefined;

  const baseParams: Partial<ListFoliosQuery> = {
    ...(author ? { author } : {}),
    ...(tag ? { tag } : {}),
    ...(tags && tags.length > 0 ? { tags } : {}),
    ...(minDurationDays !== undefined ? { minDurationDays } : {}),
    ...(maxDurationDays !== undefined ? { maxDurationDays } : {}),
    ...(q ? { q } : {}),
    ...(visibility ? { visibility } : {}),
    limit,
    ...(beSort ? { sort: beSort } : {}),
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error, refetch } =
    useInfiniteQuery({
      // queryKeys.folioList(params) 가 params 통째 키 — sort 별 캐시 자동 분리 (ADR-TR-8 확인)
      queryKey: queryKeys.folioList(baseParams),
      queryFn: ({ pageParam }) =>
        listFolios({
          ...baseParams,
          cursor: pageParam as string | undefined,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage: FolioList) => lastPage.nextCursor ?? undefined,
      enabled,
      staleTime: 1000 * 60 * 2, // 2분
      throwOnError: false,
    });

  // [목적] BE 반환 순서 그대로 사용 — 클라이언트 정렬 제거(T6)
  // [이유] BE 가 sort 기준으로 정렬한 결과를 키셋 커서로 페이지네이션하므로 FE 재정렬 불필요.
  const allFolios = data?.pages.flatMap((p) => p.items) ?? [];

  return {
    pages: data?.pages ?? [],
    allFolios,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading,
    error,
    // [목적] G1-2: MyFoliosPage/ExplorePage 가 목록 실패 시 InlineErrorState(재시도)로 refetch 를 노출.
    // [이유] 기존엔 error 만 반환해 각 페이지가 재요청 경로를 만들 수 없었다(재시도 버튼 부재).
    // [사이드 이팩트] 없음 — void 래핑(버튼 onClick 용도, Promise 대기 불필요).
    refetch: () => void refetch(),
  };
}
